import { parseQuery } from '@calendly/http-tools'

import { normalizeHexColor } from '../../../initializers/theme/normalize_text_color'

/** Calendly default body text on light surfaces (initTheme TEXT_COLOR_DEFAULT). */
const LABEL_ON_WHITE_FALLBACK_HEX = '0a2540'

/**
 * WCAG relative luminance for sRGB 6-char hex (no `#`), lowercase ok.
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function relativeLuminanceFromSrgbHex6(hex6) {
  const h = hex6.toLowerCase()
  const toLin = (u8) => {
    const c = u8 / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const R = toLin(parseInt(h.slice(0, 2), 16))
  const G = toLin(parseInt(h.slice(2, 4), 16))
  const B = toLin(parseInt(h.slice(4, 6), 16))
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

/**
 * Host `text_color` is often chosen for contrast on a tinted embed page, not on
 * the forced-white Stripe panel. If it is too light on white, use a dark default.
 */
function labelHexForWhiteStripePanel(normalizedHex6) {
  const L = relativeLuminanceFromSrgbHex6(normalizedHex6)
  return L >= 0.45 ? LABEL_ON_WHITE_FALLBACK_HEX : normalizedHex6
}

/**
 * Stripe Elements Appearance API — literals only (no CSS variables).
 * Use when the embed uses custom URL colors and the payment shell is forced to
 * a white surface: align Payment Element labels / Link copy with host `text_color`
 * when it stays legible on white; otherwise fall back to dark body text.
 *
 * @param {string} [search] — Query string (e.g. `?text_color=0a2540`). Defaults to
 *   `window.location.search` in the browser. Pass explicitly in unit tests.
 * @returns {object|undefined}
 * @see https://docs.stripe.com/elements/appearance-api
 */
export function getStripeElementsTextAppearanceFromQuery(search) {
  const searchString = typeof search === 'string'
    ? search
    : (typeof window !== 'undefined' ? window.location.search : '')

  const textColorHex = normalizeHexColor(parseQuery(searchString).text_color)
  if (!textColorHex) {
    return undefined
  }

  const color = `#${labelHexForWhiteStripePanel(textColorHex)}`

  return {
    theme: 'stripe',
    rules: {
      '.Label': {
        color,
        WebkitTextFillColor: color,
      },
      '.Link': {
        color,
        WebkitTextFillColor: color,
      },
    },
  }
}
